import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { GuideSeo } from './GuideSeo';

// ─── Demo: Betfair Connection flow ────────────────────────────────────────────

const BETFAIR_STEP_COUNT = 3;

const BetfairConnectDemo: React.FC = () => {
  const [step, setStep] = useState(0);

  useEffect(() => {
    const t = setTimeout(() => setStep(s => (s + 1) % BETFAIR_STEP_COUNT), 2800);
    return () => clearTimeout(t);
  }, [step]);

  return (
    <div className="guide-demo">
      <div className="guide-demo-label">Walkthrough — connecting Betfair</div>
      <div className="guide-demo-screen">
        <AnimatePresence mode="wait">
          {step === 0 && (
            <motion.div
              key="bf-0"
              className="guide-demo-card"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.35 }}
            >
              <div className="guide-demo-card-header">
                <span className="guide-demo-card-title">Betfair Account</span>
                <span className="guide-demo-badge guide-demo-badge--off">Not connected</span>
              </div>
              <p className="guide-demo-card-body">
                Link your Betfair account to enable in-app placement and AI Mode.
              </p>
              <motion.button
                className="guide-demo-btn guide-demo-btn--primary"
                animate={{
                  boxShadow: [
                    '0 0 0 0px rgba(245,158,11,0)',
                    '0 0 0 6px rgba(245,158,11,0.28)',
                    '0 0 0 0px rgba(245,158,11,0)',
                  ],
                }}
                transition={{ duration: 1.8, repeat: Infinity }}
              >
                Connect Betfair →
              </motion.button>
            </motion.div>
          )}

          {step === 1 && (
            <motion.div
              key="bf-1"
              className="guide-demo-card guide-demo-card--betfair"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.35 }}
            >
              <div className="guide-demo-betfair-logo">betfair</div>
              <p className="guide-demo-card-body guide-demo-card-body--center">
                <strong>GallopAI</strong> is requesting access
                <br />
                to your Betfair account
              </p>
              <motion.button
                className="guide-demo-btn guide-demo-btn--betfair"
                animate={{ scale: [1, 1.04, 1] }}
                transition={{ duration: 1.3, repeat: Infinity }}
              >
                Approve Access
              </motion.button>
            </motion.div>
          )}

          {step === 2 && (
            <motion.div
              key="bf-2"
              className="guide-demo-card"
              initial={{ opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.97 }}
              transition={{ duration: 0.35 }}
            >
              <div className="guide-demo-card-header">
                <span className="guide-demo-card-title">Betfair Account</span>
                <motion.span
                  className="guide-demo-badge guide-demo-badge--on"
                  initial={{ scale: 0.7, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ type: 'spring', stiffness: 400, damping: 20, delay: 0.15 }}
                >
                  ✓ Connected
                </motion.span>
              </div>
              <div className="guide-demo-info-row">
                <span>Balance</span>
                <span>£250.00</span>
              </div>
              <div className="guide-demo-info-row">
                <span>Exchange</span>
                <span className="guide-demo-info-val--green">Ready</span>
              </div>
              <button className="guide-demo-btn guide-demo-btn--ghost">Disconnect</button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      <div className="guide-demo-dots">
        {Array.from({ length: BETFAIR_STEP_COUNT }).map((_, i) => (
          <span key={i} className={`guide-demo-dot${i === step ? ' active' : ''}`} />
        ))}
      </div>
    </div>
  );
};

// ─── Demo: AI Mode toggle ─────────────────────────────────────────────────────

const AIModeDemo: React.FC = () => {
  const [aiOn, setAiOn] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setAiOn(v => !v), 2500);
    return () => clearTimeout(t);
  }, [aiOn]);

  return (
    <div className="guide-demo">
      <div className="guide-demo-label">Walkthrough — switching AI Mode on / off</div>
      <div className="guide-demo-screen">
        <div className="guide-demo-card" style={{ width: '100%' }}>
          <div className="guide-demo-card-header">
            <span className="guide-demo-card-title">AI Mode</span>
            <motion.button
              className={`guide-demo-toggle${aiOn ? ' is-on' : ''}`}
              aria-label="AI Mode toggle"
              layout
            >
              <motion.span
                className="guide-demo-toggle-thumb"
                layout
                transition={{ type: 'spring', stiffness: 500, damping: 30 }}
              />
            </motion.button>
          </div>

          <AnimatePresence mode="wait">
            {aiOn ? (
              <motion.p
                key="on"
                className="guide-demo-card-body guide-demo-card-body--on"
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                transition={{ duration: 0.25 }}
              >
                Qualifying bets will be placed automatically from{' '}
                <strong>11:00 UK time</strong> using your stake rules.
              </motion.p>
            ) : (
              <motion.p
                key="off"
                className="guide-demo-card-body"
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                transition={{ duration: 0.25 }}
              >
                AI Mode is off — review picks and place each bet manually from the race card.
              </motion.p>
            )}
          </AnimatePresence>

          <AnimatePresence>
            {aiOn && (
              <motion.div
                className="guide-demo-ai-status"
                initial={{ opacity: 0, height: 0, marginTop: 0 }}
                animate={{ opacity: 1, height: 'auto', marginTop: '0.75rem' }}
                exit={{ opacity: 0, height: 0, marginTop: 0 }}
                transition={{ duration: 0.3 }}
              >
                <span className="guide-demo-badge guide-demo-badge--on">Active</span>
                <span className="guide-demo-ai-status-text">Monitoring from 11:00 UK time</span>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

// ─── Demo: Placing / logging a bet ────────────────────────────────────────────

const BET_STEP_COUNT = 3;

const BetPlacementDemo: React.FC = () => {
  const [step, setStep] = useState(0);

  useEffect(() => {
    const t = setTimeout(() => setStep(s => (s + 1) % BET_STEP_COUNT), 2700);
    return () => clearTimeout(t);
  }, [step]);

  return (
    <div className="guide-demo">
      <div className="guide-demo-label">Walkthrough — placing or logging a bet</div>
      <div className="guide-demo-screen">
        <AnimatePresence mode="wait">
          {step === 0 && (
            <motion.div
              key="bp-0"
              className="guide-demo-card"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.35 }}
            >
              <div className="guide-demo-race-row">
                <div className="guide-demo-race-info">
                  <div className="guide-demo-race-horse">Thunder Bay</div>
                  <div className="guide-demo-race-meta">14:30 · Newmarket · 4.50</div>
                </div>
                <span className="guide-demo-badge guide-demo-badge--conf">HIGH</span>
              </div>
              <div className="guide-demo-btn-row">
                <motion.button
                  className="guide-demo-btn guide-demo-btn--primary"
                  animate={{
                    boxShadow: [
                      '0 0 0 0px rgba(245,158,11,0)',
                      '0 0 0 6px rgba(245,158,11,0.28)',
                      '0 0 0 0px rgba(245,158,11,0)',
                    ],
                  }}
                  transition={{ duration: 1.8, repeat: Infinity }}
                >
                  Place on Betfair
                </motion.button>
                <button className="guide-demo-btn guide-demo-btn--ghost">Log Bet</button>
              </div>
            </motion.div>
          )}

          {step === 1 && (
            <motion.div
              key="bp-1"
              className="guide-demo-card"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.35 }}
            >
              <div className="guide-demo-form-title">Place bet — Thunder Bay</div>
              <div className="guide-demo-info-row">
                <span>Odds</span>
                <span>4.50</span>
              </div>
              <div className="guide-demo-info-row">
                <span>Stake</span>
                <span className="guide-demo-input-mock">£10.00</span>
              </div>
              <motion.button
                className="guide-demo-btn guide-demo-btn--primary"
                animate={{ scale: [1, 1.04, 1] }}
                transition={{ duration: 1.3, repeat: Infinity }}
              >
                Confirm &amp; Place
              </motion.button>
            </motion.div>
          )}

          {step === 2 && (
            <motion.div
              key="bp-2"
              className="guide-demo-card guide-demo-card--success"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.35 }}
            >
              <motion.div
                className="guide-demo-success-icon"
                initial={{ scale: 0, rotate: -30 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ type: 'spring', stiffness: 400, damping: 18, delay: 0.15 }}
              >
                ✓
              </motion.div>
              <div className="guide-demo-form-title guide-demo-form-title--success">Bet placed!</div>
              <div className="guide-demo-info-row">
                <span>Thunder Bay</span>
                <span>£10.00 @ 4.50</span>
              </div>
              <div className="guide-demo-info-row guide-demo-info-row--muted">
                <span>Logged to Today&apos;s Bets</span>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      <div className="guide-demo-dots">
        {Array.from({ length: BET_STEP_COUNT }).map((_, i) => (
          <span key={i} className={`guide-demo-dot${i === step ? ' active' : ''}`} />
        ))}
      </div>
    </div>
  );
};

export const GuideGettingStarted: React.FC = () => (
  <>
    <GuideSeo
      segment="getting-started"
      title="Getting started"
      description="Learn how GallopAI fits together: Dashboard, races, bets, performance, and settings. UK horse racing betting with optional Betfair automation."
    />
    <article className="guide-article">
      <h1>Getting started</h1>
      <p className="guide-lead">
        GallopAI helps you follow UK horse racing selections, manage how bets are placed or recorded, and track results in one place. This guide explains
        how to use the app—not how picks are generated internally.
      </p>

      <section className="guide-section">
        <h2>What you will use most</h2>
        <ul>
          <li>
            <strong>Dashboard</strong> — Connect and manage your Betfair link, see account status, turn <strong>AI Mode</strong> on or off, and read
            the day&apos;s paddock-style summary when available.
          </li>
          <li>
            <strong>Today&apos;s Races</strong> — Browse meetings and race cards, read analysis for each runner, and place or log bets depending on the
            mode you use.
          </li>
          <li>
            <strong>Today&apos;s Bets</strong> — See bets placed or logged today (including pending and unmatched where relevant).
          </li>
          <li>
            <strong>Performance</strong> — Charts and history of settled activity linked to your account.
          </li>
          <li>
            <strong>Results History</strong> — Past races and outcomes alongside what the app highlighted.
          </li>
          <li>
            <strong>Settings</strong> — Profile, stake per point, Betfair commission, subscription, and optional morning digest email.
          </li>
        </ul>
      </section>

      <section className="guide-section">
        <h2>Three ways to play (modes)</h2>
        <p>
          You can run fully automated placement on Betfair (AI Mode), review picks and place from the race card with Betfair linked, or bet anywhere and
          log stakes in GallopAI. Details are in <Link to="/guide/betfair-ai-mode">Betfair &amp; AI Mode</Link> and{' '}
          <Link to="/guide/races-and-betting">Today&apos;s races &amp; betting</Link>.
        </p>
      </section>

      <div className="guide-pager guide-pager--end">
        <Link to="/guide/account-subscription" className="btn btn-primary btn-compact-trial guide-pager-btn">
          Next — Account &amp; subscription
        </Link>
      </div>
    </article>
  </>
);

export const GuideAccountSubscription: React.FC = () => (
  <>
    <GuideSeo
      segment="account-subscription"
      title="Account & subscription"
      description="Register, sign in, subscription and billing in GallopAI. Free trial, Stripe checkout, resume signup, and managing your membership."
    />
    <article className="guide-article">
      <h1>Account &amp; subscription</h1>
      <p className="guide-lead">
        Manage access to GallopAI with your email account and subscription status shown in Settings.
      </p>

      <section className="guide-section">
        <h2>Sign up and sign in</h2>
        <p>
          Use <strong>Register</strong> to create an account, or <strong>Sign in</strong> if you already have one. If you forgot your password, use the
          password reset flow from the login page.
        </p>
      </section>

      <section className="guide-section">
        <h2>Checkout and trial</h2>
        <p>
          New members typically start with a <strong>14-day free trial</strong> via the subscribe flow. After the trial, a recurring membership applies
          unless you cancel in line with the terms shown at checkout and in <Link to="/refund-policy">Refund Policy</Link>.
        </p>
        <p>
          If you paid in Stripe but did not finish creating your GallopAI login, open <strong>Resume signup</strong>, enter the same email you used at
          checkout, and follow the link to complete registration.
        </p>
      </section>

      <section className="guide-section">
        <h2>Subscription in Settings</h2>
        <p>
          Open <Link to="/settings">Settings</Link> to see your subscription status and trial or renewal date. During a{' '}
          <strong>free trial</strong>, use <strong>Cancel trial</strong> in Settings — you keep access until the trial ends and
          will not be charged. On a <strong>paid subscription</strong>, use <strong>Manage billing</strong> to update your card
          or cancel renewal through Stripe.
        </p>
      </section>

      <div className="guide-pager guide-pager--split">
        <Link to="/guide/getting-started" className="btn btn-hero-outline guide-pager-btn">
          Previous
        </Link>
        <Link to="/guide/betfair-ai-mode" className="btn btn-primary btn-compact-trial guide-pager-btn">
          Next — Betfair &amp; AI Mode
        </Link>
      </div>
    </article>
  </>
);

export const GuideBetfairAiMode: React.FC = () => (
  <>
    <GuideSeo
      segment="betfair-ai-mode"
      title="Betfair & AI Mode"
      description="Connect Betfair to GallopAI, use AI Mode for automatic placement from 11:00 UK time, stake per point, and what happens if a bet is unmatched."
    />
    <article className="guide-article">
      <h1>Betfair &amp; AI Mode</h1>
      <p className="guide-lead">
        Linking Betfair lets you place exchange bets from the app and optionally automate placement when AI Mode is on.
      </p>

      <section className="guide-section">
        <h2>Connecting Betfair</h2>
        <p>
          From the <Link to="/dashboard">Dashboard</Link>, start the Betfair connection flow and approve access in Betfair&apos;s screens. When connected,
          you should see connection status and account summary information the app is allowed to display.
        </p>
        <p>You can disconnect when you need to; automated features that depend on Betfair will stop until you connect again.</p>
        <BetfairConnectDemo />
      </section>

      <section className="guide-section">
        <h2>AI Mode</h2>
        <p>
          <strong>AI Mode</strong> is optional. When it is on and your account meets the requirements (including an active Betfair link), the system can
          place bets on qualifying daily selections automatically from <strong>11:00 UK time</strong> onward, using your stake rules—not manual clicks each
          time.
        </p>
        <p>Turn AI Mode off anytime from the Dashboard if you prefer to review and place bets yourself.</p>
        <AIModeDemo />
        <div className="guide-note">
          <strong>Stakes.</strong> Stake size uses your <strong>stake per point</strong> together with each selection&apos;s suggested stake in points.
          Configure stake per point in <Link to="/settings">Settings</Link> (minimum values apply).
        </div>
      </section>

      <section className="guide-section">
        <h2>Unmatched bets (AI Mode)</h2>
        <p>
          On the exchange, a bet may not match immediately. The system can retry a limited number of times, then retry on a short interval up until race
          start to seek liquidity—see <Link to="/faq">FAQ</Link> for the exact behaviour in your environment.
        </p>
      </section>

      <section className="guide-section">
        <h2>What we do not publish here</h2>
        <p>
          How selections are ranked or weighted internally is proprietary. This guide only covers <em>how to operate</em> GallopAI safely and consistently.
        </p>
      </section>

      <div className="guide-pager guide-pager--split">
        <Link to="/guide/account-subscription" className="btn btn-hero-outline guide-pager-btn">
          Previous
        </Link>
        <Link to="/guide/races-and-betting" className="btn btn-primary btn-compact-trial guide-pager-btn">
          Next — Today&apos;s races &amp; betting
        </Link>
      </div>
    </article>
  </>
);

export const GuideRacesAndBetting: React.FC = () => (
  <>
    <GuideSeo
      segment="races-and-betting"
      title="Today's races & betting"
      description="Use Today's Races and race cards in GallopAI: analysis, placing on Betfair from the card, logging manual bets, and when betting is disabled."
    />
    <article className="guide-article">
      <h1>Today&apos;s races &amp; betting</h1>
      <p className="guide-lead">
        The <Link to="/today-races">Today&apos;s Races</Link> area lists meetings and races with selections and write-ups so you can act in the way you
        prefer.
      </p>

      <section className="guide-section">
        <h2>Race cards and analysis</h2>
        <p>Open a meeting or race to expand details. Each card includes readable analysis so you know what the app is highlighting and why—in plain language.</p>
      </section>

      <section className="guide-section">
        <h2>Placing or logging bets</h2>
        <ul>
          <li>
            <strong>Betfair linked, AI Mode off</strong> — Review each pick and use the in-app actions to place on Betfair from the race card with current
            prices where shown.
          </li>
          <li>
            <strong>AI Mode on</strong> — Qualifying bets are placed for you on the schedule described in <Link to="/guide/betfair-ai-mode">Betfair &amp; AI Mode</Link>.
          </li>
          <li>
            <strong>Manual logging</strong> — If you used another bookmaker or channel, record the bet in GallopAI so your record and P&amp;L stay in one
            place.
          </li>
        </ul>
        <BetPlacementDemo />
      </section>

      <section className="guide-section">
        <h2>After the race has started</h2>
        <p>Once a race has gone off, actions that would place new bets on that race are disabled—your record and results views still update as normal.</p>
      </section>

      <section className="guide-section">
        <h2>No qualifying picks</h2>
        <p>
          On days with nothing that meets the service criteria, you may see an empty state on Today&apos;s Races. That is expected; check back on future
          cards.
        </p>
      </section>

      <div className="guide-pager guide-pager--split">
        <Link to="/guide/betfair-ai-mode" className="btn btn-hero-outline guide-pager-btn">
          Previous
        </Link>
        <Link to="/guide/performance-history" className="btn btn-primary btn-compact-trial guide-pager-btn">
          Next — Performance &amp; history
        </Link>
      </div>
    </article>
  </>
);

export const GuidePerformanceHistory: React.FC = () => (
  <>
    <GuideSeo
      segment="performance-history"
      title="Performance & history"
      description="Performance charts, bet history, Today's Bets, and Results History in GallopAI. Track P&L and settled results over time."
    />
    <article className="guide-article">
      <h1>Performance &amp; history</h1>
      <p className="guide-lead">
        Use <Link to="/performance">Performance</Link>, <Link to="/today-bets">Today&apos;s Bets</Link>, and <Link to="/results">Results History</Link>{' '}
        to review how activity is tracking.
      </p>

      <section className="guide-section">
        <h2>Performance</h2>
        <p>
          See summaries and charts for settled betting activity: trends, win rates, and profit or loss views depending on what the page shows for your
          account. Past results do not guarantee future returns.
        </p>
      </section>

      <section className="guide-section">
        <h2>Today&apos;s Bets</h2>
        <p>
          Lists bets from today&apos;s window—pending, unmatched, won, lost, or void—so you can audit the current day without mixing in older settled
          history.
        </p>
      </section>

      <section className="guide-section">
        <h2>Results History</h2>
        <p>Browse older races and outcomes next to what GallopAI surfaced, useful when you mix automated, in-app Betfair, and manually logged bets.</p>
      </section>

      <div className="guide-pager guide-pager--split">
        <Link to="/guide/races-and-betting" className="btn btn-hero-outline guide-pager-btn">
          Previous
        </Link>
        <Link to="/guide/settings-digest" className="btn btn-primary btn-compact-trial guide-pager-btn">
          Next — Settings &amp; morning digest
        </Link>
      </div>
    </article>
  </>
);

export const GuideSettingsDigest: React.FC = () => (
  <>
    <GuideSeo
      segment="settings-digest"
      title="Settings & morning digest"
      description="Profile, stake per point, Betfair commission, currency, email notifications, and morning digest email in GallopAI Settings."
    />
    <article className="guide-article">
      <h1>Settings &amp; morning digest</h1>
      <p className="guide-lead">
        Open <Link to="/settings">Settings</Link> from your avatar menu to manage profile, staking, and email preferences.
      </p>

      <section className="guide-section">
        <h2>Profile</h2>
        <p>Choose a display name and avatar where available so your account feels like yours in the header.</p>
      </section>

      <section className="guide-section">
        <h2>Betting settings</h2>
        <p>
          Set <strong>stake per point</strong> (used with suggested stakes in points), <strong>Betfair commission</strong> for your exchange usage, and{' '}
          <strong>currency</strong> where the app supports it. Save changes when prompted.
        </p>
      </section>

      <section className="guide-section">
        <h2>Subscription</h2>
        <p>
          Review membership status in Settings. Free trials can be cancelled there without opening Stripe. Paid members use{' '}
          <strong>Manage billing</strong> for payment methods, invoices, and cancellation.
        </p>
      </section>

      <section className="guide-section">
        <h2>Morning digest email</h2>
        <p>
          Opt in or out of the morning digest so you receive a summary email when the product sends one. This is separate from critical account emails.
        </p>
      </section>

      <div className="guide-pager guide-pager--split">
        <Link to="/guide/performance-history" className="btn btn-hero-outline guide-pager-btn">
          Previous
        </Link>
        <Link to="/guide/troubleshooting" className="btn btn-primary btn-compact-trial guide-pager-btn">
          Next — Troubleshooting
        </Link>
      </div>
    </article>
  </>
);

export const GuideTroubleshooting: React.FC = () => (
  <>
    <GuideSeo
      segment="troubleshooting"
      title="Troubleshooting"
      description="Get help with GallopAI: FAQ, contact support, gambling support resources, and common issues."
    />
    <article className="guide-article">
      <h1>Troubleshooting</h1>
      <p className="guide-lead">
        If something looks wrong, work through the checks below. For account-specific problems, contact support with your registered email.
      </p>

      <section className="guide-section">
        <h2>Before you write in</h2>
        <ul>
          <li>
            Confirm you are signed in and your subscription is active where required for paid features (see <Link to="/settings">Settings</Link>).
          </li>
          <li>
            For Betfair issues, confirm you completed connection on the <Link to="/dashboard">Dashboard</Link> and that AI Mode matches how you intend to
            bet.
          </li>
          <li>
            Read the <Link to="/faq">FAQ</Link> for staking, trials, refunds, and technical pointers.
          </li>
        </ul>
      </section>

      <section className="guide-section">
        <h2>Contact</h2>
        <p>
          Use the <Link to="/contact">Contact</Link> page to reach the team. Response times are described there.
        </p>
      </section>

      <section className="guide-section">
        <h2>Gambling support</h2>
        <p>
          If gambling is causing harm, use resources linked in the site footer (e.g. BeGambleAware, GAMSTOP, GamCare). Only bet what you can afford to
          lose.
        </p>
      </section>

      <div className="guide-pager guide-pager--split">
        <Link to="/guide/settings-digest" className="btn btn-hero-outline guide-pager-btn">
          Previous
        </Link>
        <Link to="/guide/getting-started" className="btn btn-primary btn-compact-trial guide-pager-btn">
          Back to start
        </Link>
      </div>
    </article>
  </>
);
